import { i18n } from "../../translate/i18n";

// Regras puras da entrada de /tickets (mockup "Tela Aeros Dark - Atendimentos
// Nova Entrada"): o painel do dia que ocupa o lugar do antigo "Selecione um
// ticket" e o cartão de contexto do atendimento selecionado.
//
// Ficam fora dos componentes para serem testáveis sem renderizar a tela. Os
// testes fixam i18n.changeLanguage("pt") no beforeAll.

// Minutos sem resposta a partir dos quais uma conversa entra em "Precisa de
// atenção". O mockup mostra "sem resposta há mais de 10 minutos".
export const LIMITE_SEM_RESPOSTA_MIN = 10;

/**
 * Saudação do cabeçalho pelo horário local. Só o primeiro nome: "Bom dia,
 * Juliana Pereira dos Santos." quebraria a linha do mockup.
 */
export const greeting = (name, now = new Date()) => {
  const hora = now.getHours();
  const chave =
    hora < 12 ? "morning" : hora < 18 ? "afternoon" : "evening";
  const primeiro = String(name || "").trim().split(/\s+/)[0] || "";
  return i18n.t(`aeros.ticketsEntry.greeting.${chave}`, { name: primeiro });
};

/**
 * Iniciais para o avatar (no máximo duas). Ignora partículas ("de", "da",
 * "dos"...) para que "Mercado Bom Preço" vire MB e não MB do jeito errado.
 */
export const initials = (name) => {
  const particulas = ["de", "da", "do", "das", "dos", "e"];
  const partes = String(name || "")
    .trim()
    .split(/\s+/)
    .filter(p => p && !particulas.includes(p.toLowerCase()));
  if (!partes.length) return "?";
  return partes
    .slice(0, 2)
    .map(p => p[0].toUpperCase())
    .join("");
};

/**
 * "14 min", "2 h 5 min", "3 d". Recebe segundos e devolve rótulo curto.
 *
 * Devolve null para entrada ausente — quem chama decide se some com o bloco.
 * Nunca inventa "0 min" para dado que não veio: o mockup usa "—" para fila sem
 * ninguém aguardando, e isso é diferente de "esperou zero".
 */
export const formatWait = (seconds) => {
  if (seconds === null || seconds === undefined) return null;
  const s = Number(seconds);
  if (!Number.isFinite(s) || s < 0) return null;

  const min = Math.floor(s / 60);
  if (min < 1) return i18n.t("aeros.ticketsEntry.wait.now");
  if (min < 60) return i18n.t("aeros.ticketsEntry.wait.minutes", { count: min });

  const horas = Math.floor(min / 60);
  if (horas < 24) {
    const resto = min % 60;
    return resto
      ? i18n.t("aeros.ticketsEntry.wait.hoursMinutes", { hours: horas, minutes: resto })
      : i18n.t("aeros.ticketsEntry.wait.hours", { count: horas });
  }

  return i18n.t("aeros.ticketsEntry.wait.days", { count: Math.floor(horas / 24) });
};

/**
 * "cliente desde 2024" — só o ano, como no mockup. Data ausente ou inválida
 * devolve null e o trecho some do subtítulo.
 */
export const customerSince = (createdAt) => {
  if (!createdAt) return null;
  const d = createdAt instanceof Date ? createdAt : new Date(createdAt);
  if (Number.isNaN(d.getTime())) return null;
  return i18n.t("aeros.ticketsEntry.context.since", { year: d.getFullYear() });
};

/**
 * "há 22 dias" para o cartão "Último contato". Menos de um dia vira "hoje".
 */
export const lastContactLabel = (lastTicketAt, now = new Date()) => {
  if (!lastTicketAt) return null;
  const d = lastTicketAt instanceof Date ? lastTicketAt : new Date(lastTicketAt);
  if (Number.isNaN(d.getTime())) return null;

  const dias = Math.floor((now.getTime() - d.getTime()) / (24 * 60 * 60 * 1000));
  if (dias <= 0) return i18n.t("aeros.ticketsEntry.context.today");
  return i18n.t("aeros.ticketsEntry.context.daysAgo", { count: dias });
};

/**
 * Conversas do atendente paradas sem resposta, da mais antiga para a mais
 * recente — é a lista por trás de "Precisa de atenção", e o clique no card
 * abre a primeira.
 *
 * `fromMe: false` no ticket significa que a última mensagem foi do contato —
 * então a bola está com o atendente. Tickets já respondidos (fromMe true) e os
 * que não são do usuário ficam de fora. O backend precisa manter `fromMe` em
 * TODO caminho de envio e recebimento (Baileys e Cloud API); quando só o
 * listener do Baileys gravava, o canal oficial contava conversa já respondida.
 *
 * ATENÇÃO: só enxerga os tickets que a lista da esquerda já carregou. É uma
 * amostra da primeira página, não o total da empresa — por isso a tela rotula
 * o número como das "suas conversas", nunca como total.
 */
export const conversasSemResposta = (tickets, userId, now = new Date(), limiteMin = LIMITE_SEM_RESPOSTA_MIN) => {
  if (!Array.isArray(tickets)) return [];
  const limiteMs = limiteMin * 60 * 1000;

  return tickets
    .map(t => {
      if (!t || t.status !== "open") return null;
      if (userId !== undefined && userId !== null && Number(t.userId) !== Number(userId)) return null;
      if (t.fromMe) return null;
      const d = t.updatedAt instanceof Date ? t.updatedAt : new Date(t.updatedAt);
      if (Number.isNaN(d.getTime())) return null;
      const parado = now.getTime() - d.getTime();
      return parado >= limiteMs ? { ticket: t, parado } : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.parado - a.parado)
    .map(x => x.ticket);
};

export const semRespostaHa = (tickets, userId, now = new Date(), limiteMin = LIMITE_SEM_RESPOSTA_MIN) =>
  conversasSemResposta(tickets, userId, now, limiteMin).length;

/**
 * Agendamentos de hoje, em ordem de horário — o card "Agenda de hoje".
 *
 * Vem de `GET /schedules/summary`, que devolve os próximos envios a partir de
 * hoje (e completa com falhas recentes quando sobra espaço). O filtro de dia
 * mora aqui: uma falha de ontem não é compromisso de hoje.
 */
export const agendaDeHoje = (upcoming, now = new Date()) => {
  if (!Array.isArray(upcoming)) return [];
  const inicio = new Date(now);
  inicio.setHours(0, 0, 0, 0);
  const fim = new Date(inicio.getTime() + 24 * 60 * 60 * 1000);

  return upcoming
    .filter(s => {
      const d = s?.sendAt ? new Date(s.sendAt) : null;
      return d && !Number.isNaN(d.getTime()) && d >= inicio && d < fim;
    })
    .sort((a, b) => new Date(a.sendAt) - new Date(b.sendAt));
};

/**
 * Tom da fila pela quantidade aguardando e pela espera mais antiga — é o que
 * pinta o número de vermelho/âmbar no card "Suas filas agora".
 */
export const queueTone = (waiting, oldestWaitSeconds) => {
  if (!waiting) return "idle";
  const min = (oldestWaitSeconds ?? 0) / 60;
  if (min >= 10) return "urgent";
  if (min >= 5) return "warn";
  return "normal";
};

/**
 * As linhas de "Precisa de atenção". Cada uma só entra quando tem contagem —
 * o card não mostra "0 conversas sem resposta", ele omite a linha; sem
 * nenhuma linha, quem chama esconde o card inteiro.
 */
export const atencaoItems = ({ semResposta = 0 } = {}) => {
  const itens = [];
  if (semResposta > 0) {
    itens.push({
      key: "semResposta",
      tone: "error",
      icon: "priority_high",
      text: i18n.t("aeros.ticketsEntry.attention.noReply", {
        count: semResposta,
        minutes: LIMITE_SEM_RESPOSTA_MIN
      })
    });
  }
  return itens;
};
